// Web Sources Page - Dedicated page for website scraping and entity extraction
import { useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Globe, Loader2, ExternalLink, Trash2, RefreshCw, CheckCircle, XCircle, Clock, ArrowLeft, Eye } from 'lucide-react'
import { useSessionStore } from '@/stores/sessionStore'
import { webSourcesApi, type WebSource, type CrawlConfig } from '@/lib/api/client'
import { WebSourceUploader } from './WebSourceUploader'
import { WebCrawlProgress } from './WebCrawlProgress'
import { SourceDetailView } from './SourceDetailView'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export function WebSourcesPage() {
  const { accountId, projectId } = useSessionStore()
  const [activeWebSourceId, setActiveWebSourceId] = useState<string | null>(null)
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch existing web sources
  const {
    data: webSources,
    isLoading: isLoadingList,
    refetch,
  } = useQuery({
    queryKey: ['web-sources', accountId, projectId],
    queryFn: () => webSourcesApi.list(accountId!, projectId!),
    enabled: !!accountId && !!projectId,
  })

  // Handle submit
  const handleSubmit = useCallback(
    async (url: string, crawlConfig?: CrawlConfig) => {
      if (!accountId || !projectId) {
        setError('Account or project not selected')
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const result = await webSourcesApi.scrape(accountId, projectId, { url, crawlConfig })
        if (result.webSourceId) {
          setActiveWebSourceId(result.webSourceId)
          refetch()
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to start web scrape')
      } finally {
        setIsLoading(false)
      }
    },
    [accountId, projectId, refetch]
  )

  // Handle crawl completion
  const handleCrawlComplete = useCallback(() => {
    setActiveWebSourceId(null)
    refetch()
  }, [refetch])

  // Handle delete
  const handleDelete = async (id: string) => {
    if (!accountId || !projectId) return
    try {
      await webSourcesApi.delete(accountId, projectId, id)
      refetch()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete web source')
    }
  }

  const getStatusIcon = (status: WebSource['crawlStatus']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />
      case 'fetching':
      case 'extracting':
        return <Loader2 className="h-4 w-4 text-business animate-spin" />
      default:
        return <Clock className="h-4 w-4 text-text-muted" />
    }
  }

  const getStatusLabel = (status: WebSource['crawlStatus']) => {
    switch (status) {
      case 'completed':
        return 'Completed'
      case 'error':
        return 'Failed'
      case 'fetching':
        return 'Crawling...'
      case 'extracting':
        return 'Extracting...'
      default:
        return 'Pending'
    }
  }

  // If a source is selected, show the detail view
  if (selectedSourceId) {
    return (
      <SourceDetailView
        sourceId={selectedSourceId}
        onBack={() => setSelectedSourceId(null)}
      />
    )
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-warm-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-market/10 flex items-center justify-center">
              <Globe className="w-5 h-5 text-market" />
            </div>
            <div>
              <h1 className="text-xl font-display font-semibold text-text">Web Sources</h1>
              <p className="text-sm text-text-secondary">
                Scrape websites to extract business intelligence
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Upload Section */}
          <div className="bg-white rounded-xl border border-warm-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Add Website</h2>
            {activeWebSourceId && accountId && projectId ? (
              <WebCrawlProgress
                webSourceId={activeWebSourceId}
                accountId={accountId}
                projectId={projectId}
                onComplete={handleCrawlComplete}
              />
            ) : (
              <WebSourceUploader
                onSubmit={handleSubmit}
                isLoading={isLoading}
                error={error}
              />
            )}
          </div>

          {/* Existing Web Sources */}
          <div className="bg-white rounded-xl border border-warm-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Scraped Websites</h2>
            {isLoadingList ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-text-muted" />
              </div>
            ) : !webSources || webSources.length === 0 ? (
              <div className="text-center py-8 text-text-secondary">
                <Globe className="h-10 w-10 mx-auto mb-3 text-text-muted" />
                <p>No websites scraped yet</p>
                <p className="text-sm text-text-muted mt-1">Add a URL above to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {webSources.map((source) => {
                  const canView = source.crawlStatus === 'completed' && source.sourceId
                  return (
                    <div
                      key={source.id}
                      className={cn(
                        'flex items-center gap-4 p-4 rounded-lg border transition-colors',
                        source.crawlStatus === 'error'
                          ? 'border-red-200 bg-red-50'
                          : canView
                            ? 'border-warm-200 hover:bg-warm-50 cursor-pointer'
                            : 'border-warm-200'
                      )}
                      onClick={() => canView && setSelectedSourceId(source.sourceId)}
                    >
                      <div className="flex-shrink-0">{getStatusIcon(source.crawlStatus)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-text truncate">
                            {source.title || source.domain || source.url}
                          </span>
                          <a
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-text-muted hover:text-text"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-text-secondary">
                          <span>{source.domain}</span>
                          <span className="text-text-muted">·</span>
                          <span>{getStatusLabel(source.crawlStatus)}</span>
                          {source.crawlProgress && (
                            <>
                              <span className="text-text-muted">·</span>
                              <span>{source.crawlProgress.pagesCrawled} pages</span>
                            </>
                          )}
                        </div>
                        {source.errorMessage && (
                          <p className="text-sm text-red-600 mt-1">{source.errorMessage}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {canView && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedSourceId(source.sourceId)
                            }}
                            className="text-text-muted hover:text-business"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDelete(source.id)
                          }}
                          className="text-text-muted hover:text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
